/**
 * Company › Billing (ADR-022 §3) — a read-only roll-up per linked workspace
 * for one month. It shows only what the ledger attributes to a workspace:
 * document storage footprint, asset-library charges billed to the payer,
 * and activity counts. Member AI usage bills to each member under the v1
 * nominated-payer model, and the section says so rather than inventing a
 * total.
 */

import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useCompanyBilling } from '../../hooks/useCompanies';
import { describeError } from './shared';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function credits(n: number): string {
  return `${n.toFixed(2)} cr`;
}

/** The current month and the five before it, newest first, as YYYY-MM. */
function recentMonths(now = new Date()): string[] {
  const out: string[] = [];
  for (let i = 0; i < 6; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const band =
  '-mx-6 flex items-center justify-between border-y border-border bg-muted px-6 py-2 text-sm font-semibold text-foreground';

export function BillingSection({ companyId }: { companyId: string }) {
  const months = useMemo(() => recentMonths(), []);
  const [month, setMonth] = useState(months[0]);
  const billing = useCompanyBilling(companyId, month);
  const data = billing.data;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        What the Company&apos;s payer is billed for, per linked workspace. Storage and asset
        libraries bill to the payer; each member&apos;s own AI usage bills to that member.
      </p>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="billing-month">Month</Label>
          <select
            id="billing-month"
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m)}
              </option>
            ))}
          </select>
        </div>
        {data && (
          <p className="text-sm text-muted-foreground">
            Payer:{' '}
            <span className="text-foreground">
              {data.payer.label ?? `user …${data.payer.userId.slice(-6)}`}
            </span>
          </p>
        )}
      </div>

      {billing.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : billing.isError || !data ? (
        <p className="text-sm text-destructive">{describeError(billing.error)}</p>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{monthLabel(data.month)}</CardTitle>
            <CardDescription>
              {data.workspaces.length === 0
                ? 'No workspaces are linked to this Company.'
                : `${data.workspaces.length} linked workspace(s).`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <section className="space-y-2">
              <h3 className={band}>
                <span>Billed to the payer</span>
                <span className="font-normal text-muted-foreground">
                  {credits(data.totals.assetLibraryChargedCredits)}
                </span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-1 pr-3 font-medium">Workspace</th>
                      <th className="py-1 pr-3 font-medium">Documents</th>
                      <th className="py-1 pr-3 font-medium">Asset library</th>
                      <th className="py-1 font-medium text-right">Charged</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.workspaces.map((w) => (
                      <tr key={w.holderId}>
                        <td className="py-2 pr-3 font-medium text-foreground">{w.displayName}</td>
                        <td className="py-2 pr-3 tabular-nums">
                          {formatBytes(w.storage.totalBytes)}
                          <span className="text-muted-foreground">
                            {' '}
                            · {w.storage.fileCount} file(s)
                          </span>
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {w.assetLibrary.byteDays > 0
                            ? `${formatBytes(w.assetLibrary.byteDays)}·days`
                            : '—'}
                          {w.assetLibrary.pendingSnapshots > 0 && (
                            <span className="text-muted-foreground">
                              {' '}
                              · {w.assetLibrary.pendingSnapshots} pending
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          {credits(w.assetLibrary.chargedCredits)}
                          {w.assetLibrary.chargedToOthersCredits > 0 && (
                            <span
                              className="block text-xs text-muted-foreground"
                              title="Charged this month to a previous payer or the workspace's own owner (before it linked)"
                            >
                              +{credits(w.assetLibrary.chargedToOthersCredits)} to others
                            </span>
                          )}
                          {w.assetLibrary.chargedUnattributedCredits > 0 && (
                            <span
                              className="block text-xs text-muted-foreground"
                              title="Charged before payer tracking; billed to whoever was the workspace's payer at the time"
                            >
                              +{credits(w.assetLibrary.chargedUnattributedCredits)} before payer
                              tracking
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border font-medium">
                      <td className="py-2 pr-3">Total</td>
                      <td className="py-2 pr-3 tabular-nums">
                        {formatBytes(data.totals.storageBytes)}
                      </td>
                      <td className="py-2 pr-3" />
                      <td className="py-2 text-right tabular-nums">
                        {credits(data.totals.assetLibraryChargedCredits)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Document storage is shown as a footprint; the limit that applies is the payer&apos;s
                plan, pooled across everything they pay for. It is not charged per month today.
                Asset-library storage is charged daily to the payer from snapshots.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className={band}>
                <span>Activity in these workspaces</span>
                <span className="font-normal text-muted-foreground">
                  {data.totals.invocations} call(s)
                </span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-1 pr-3 font-medium">Workspace</th>
                      <th className="py-1 pr-3 font-medium text-right">Calls</th>
                      <th className="py-1 pr-3 font-medium text-right">Succeeded</th>
                      <th className="py-1 pr-3 font-medium text-right">Failed</th>
                      <th className="py-1 font-medium text-right">Members active</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.workspaces.map((w) => (
                      <tr key={w.holderId}>
                        <td className="py-2 pr-3 font-medium text-foreground">{w.displayName}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {w.activity.invocations}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {w.activity.successes}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{w.activity.failures}</td>
                        <td className="py-2 text-right tabular-nums">{w.activity.members}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Counts of entity calls made in each workspace (arche reads, AI, integrations). Their
                credits are charged to the member who made them, not to the Company.
              </p>
            </section>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
